import React from 'react';
import { Button, Avatar } from '../ui';
import type { PartnerUser } from '../../hooks/requests/queries/use_all_users_query';

interface UserSelectorProps {
  users: PartnerUser[];
  selectedUser: PartnerUser | null;
  onSelectUser: (user: PartnerUser) => void;
  onCreateUser: () => void;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  users,
  selectedUser,
  onSelectUser,
  onCreateUser,
}) => (
    <div className="au-card p-6">
      {/* Header - matches dashboard card headers */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-au-text-primary">Ally Voice Session</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-au-text-tertiary">{users.length} users</span>
          <Button variant="secondary" size="sm" onClick={onCreateUser}>
            + New user
          </Button>
        </div>
      </div>

      {/* User selector row */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <select
              value={selectedUser?.user_id || ''}
              onChange={(e) => {
                const user = users.find((u) => u.user_id === e.target.value);
                if (user) onSelectUser(user);
              }}
              className="au-input w-full cursor-pointer appearance-none pr-8"
            >
              <option value="">Select a user...</option>
              {users.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.first_name || user.user_id} - {user.company_name || user.company_id}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-au-text-tertiary">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Selected user info */}
        {selectedUser && (
          <div className="flex items-center gap-3 pl-4 border-l border-au-divider">
            <Avatar size="sm" name={selectedUser.first_name || selectedUser.user_id} />
            <div className="min-w-0">
              <div className="font-medium text-sm text-au-text-primary truncate">
                {selectedUser.first_name || selectedUser.user_id}
              </div>
              <div className="text-sm text-au-text-secondary truncate">
                {selectedUser.company_name || selectedUser.company_id}
                {selectedUser.country ? ` · ${selectedUser.country}` : ''}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

export default UserSelector;
